import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import axios from "axios";
import { API } from "../config";

const CustomerAuthContext = createContext();
export const useCustomerAuth = () => useContext(CustomerAuthContext);

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [customerToken, setCustomerToken] = useState(() => sessionStorage.getItem("ukok-customer-token") || null);
  const [loading, setLoading] = useState(true);

  const customerAxios = useMemo(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });
    instance.interceptors.request.use((config) => {
      if (customerToken) config.headers.Authorization = `Bearer ${customerToken}`;
      return config;
    });
    return instance;
  }, [customerToken]);

  const checkAuth = useCallback(async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!customerToken) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/customer/me`, {
        headers: { Authorization: `Bearer ${customerToken}` }
      });
      setCustomer(res.data);
    } catch {
      setCustomer(null);
      setCustomerToken(null);
      sessionStorage.removeItem("ukok-customer-token");
    }
    setLoading(false);
  }, [customerToken]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const loginCustomer = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/customer/login`, { email, password }, { withCredentials: true });
    setCustomer(res.data);
    setCustomerToken(res.data.token);
    sessionStorage.setItem("ukok-customer-token", res.data.token);
    return res.data;
  }, []);

  const registerCustomer = useCallback(async (email, password, name, phone) => {
    const res = await axios.post(`${API}/customer/register`, { email, password, name, phone }, { withCredentials: true });
    setCustomer(res.data);
    setCustomerToken(res.data.token);
    sessionStorage.setItem("ukok-customer-token", res.data.token);
    return res.data;
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/customer/google-session`, { session_id: sessionId }, { withCredentials: true });
    setCustomer(res.data);
    setCustomerToken(res.data.token);
    sessionStorage.setItem("ukok-customer-token", res.data.token);
    return res.data;
  }, []);

  const logoutCustomer = useCallback(async () => {
    try { await axios.post(`${API}/customer/logout`, {}, { withCredentials: true }); } catch {}
    setCustomer(null);
    setCustomerToken(null);
    sessionStorage.removeItem("ukok-customer-token");
  }, []);

  const refreshCustomer = useCallback(async () => {
    if (!customerToken) return;
    try {
      const res = await axios.get(`${API}/customer/me`, { headers: { Authorization: `Bearer ${customerToken}` } });
      setCustomer(res.data);
    } catch {}
  }, [customerToken]);

  const value = useMemo(() => ({
    customer, customerToken, loading, customerAxios,
    loginCustomer, registerCustomer, loginWithGoogle, logoutCustomer, refreshCustomer
  }), [customer, customerToken, loading, customerAxios, loginCustomer, registerCustomer, loginWithGoogle, logoutCustomer, refreshCustomer]);

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};
